import React, { useState, useEffect, useCallback } from 'react';
import { getAdminLedger, getAdminLedgerSummary } from '../../utils/api';
import Pagination from '../Pagination';

const AdminLedger = ({ config, flash, f: fProp, BC: BCProp, SYM: SYMProp, fBC, vBC }) => {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Money fields (usdtAmount, totalUsdt, liquidityAfter) are stored in USDT.
  // Use fBC/vBC (from AdminPanel) to convert to the base currency (BNB) so the
  // "BNB Amount" label matches the value. Fall back to a local formatter for
  // token/supply/price figures.
  const f = fProp || ((n, d = 4) => (n == null || isNaN(n)) ? '0' : parseFloat(n).toFixed(d));
  const BC = BCProp || config?.baseCurrency || 'USDT';
  const SYM = SYMProp || config?.tokenSymbol || 'ROBO';
  const mBC = (usdt, d = 4) => (fBC ? fBC(usdt, d) : f(usdt, d));   // money → base currency
  const mV = (usdt, d = 4) => (vBC ? vBC(usdt, d) : `${f(usdt, d)} ${BC}`); // money → "BNB ≈ USDT"
  const tr = (a) => a ? `${a.slice(0, 8)}...${a.slice(-4)}` : '—';
  // Show the Sponsor ID (referral code); fall back to a truncated wallet. Never the ROBO ID.
  const ident = (code, wallet) => code || (wallet ? `${wallet.slice(0, 8)}...${wallet.slice(-4)}` : '—');

  const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'liquidity_in', label: 'Liquidity In' },
    { key: 'liquidity_out', label: 'Liquidity Out' },
    { key: 'level_income', label: 'Level Income' },
    { key: 'level_income_miss', label: 'Level Missed' },
    { key: 'level_income_claim', label: 'Claims' },
    { key: 'level_income_claim_request', label: 'Claim Requests' },
    { key: 'royalty_in', label: 'Royalty' },
    { key: 'buy_burn', label: 'Buy Burn' },
    { key: 'sell_token_burn', label: 'Sell Burn' },
    { key: 'sell_payout', label: 'Sell Payout' },
    { key: 'sell_fee_burn', label: 'Sell Fee' },
    { key: 'admin_token', label: 'Admin Pool' },
    { key: 'admin_distribute', label: 'Admin Dist' },
  ];

  const loadLedger = useCallback(async (entryType = 'all', pg = 1, q = '') => {
    setLoading(true);
    try {
      const typeParam = entryType !== 'all' ? entryType : '';
      const data = await getAdminLedger(typeParam, q, pg);
      setEntries(data.entries || []);
      setTotalPages(data.pagination?.pages || 1);
    } catch (e) { console.error('ledger load error:', e); }
    setLoading(false);
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const data = await getAdminLedgerSummary();
      if (data.success) setSummary(data.summary);
    } catch {}
  }, []);

  useEffect(() => { loadLedger(filter, 1, search); loadSummary(); }, [filter, search, loadLedger, loadSummary]);

  const handlePage = (pg) => { setPage(pg); loadLedger(filter, pg, search); };
  const doSearch = () => { setPage(1); setSearch(searchInput.trim()); };
  const clearSearch = () => { setSearchInput(''); setPage(1); setSearch(''); };

  // Color code entry types
  const entryColor = (type) => {
    if (type.includes('liquidity_in') || type.includes('royalty')) return 'text-green';
    if (type.includes('liquidity_out') || type.includes('sell_payout')) return 'text-red';
    if (type.includes('burn') || type.includes('sell_fee')) return 'text-red';
    if (type.includes('level_income_miss')) return 'text-3';
    if (type.includes('level_income')) return 'text-accent';
    if (type.includes('admin')) return 'text-3';
    return '';
  };

  const entryIcon = (type) => {
    if (type.includes('liquidity_in')) return '↓';
    if (type.includes('liquidity_out')) return '↑';
    if (type.includes('level_income_miss')) return '✕';
    if (type.includes('level_income')) return '→';
    if (type.includes('royalty')) return '♦';
    if (type.includes('burn') || type.includes('sell_fee')) return '🔥';
    if (type.includes('sell_payout')) return '↑';
    if (type.includes('admin')) return '⊞';
    if (type.includes('claim')) return '✎';
    return '•';
  };

  return (
    <div className="fade-in">
      <h2 className="adm-title">Ledger & Transactions</h2>

      <div className="adm-user-search" style={{ marginBottom: 16 }}>
        <input className="input" placeholder="Search wallet address or Sponsor ID..." value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') doSearch(); }} />
        <button className="btn btn-primary" onClick={doSearch}>Search</button>
        {search && <button className="btn btn-outline btn-sm" onClick={clearSearch}>Clear</button>}
      </div>

      {/* Pool Summary */}
      {summary && (
        <div className="adm-kpi-grid">
          {Object.entries(summary).map(([type, data]) => (
            <div key={type} className="adm-kpi card">
              <span className="adm-kpi-label">{type.replace(/_/g, ' ').toUpperCase()}</span>
              <strong className="adm-kpi-val mono">
                {data.totalUsdt > 0 ? mV(data.totalUsdt, 4) : ''}
                {data.totalUsdt > 0 && data.totalTokens > 0 ? ' | ' : ''}
                {data.totalTokens > 0 ? `${f(data.totalTokens, 2)} ${SYM}` : ''}
              </strong>
              <span className="adm-kpi-sub">{data.count} entries</span>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="adm-ledger-filters">
        {FILTERS.map(fl => (
          <button key={fl.key} className={`d-range-btn ${filter === fl.key ? 'active' : ''}`} onClick={() => { setFilter(fl.key); setPage(1); }}>
            {fl.label}
          </button>
        ))}
      </div>

      {/* Entries Table */}
      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : entries.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th title="The member whose action caused this entry (shown by referral code)">From (Referral Code)</th>
                <th title="The member who received / earned from this entry (shown by referral code)">To (Referral Code)</th>
                <th>Level</th>
                <th>Amount ({BC})</th>
                <th>Tokens ({SYM})</th>
                <th>Token Price After</th>
                <th>Total Supply After</th>
                <th>Liquidity After</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {entries.map(e => (
                <tr key={e._id}>
                  <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{new Date(e.createdAt).toLocaleString('en-US', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true })}</td>
                  <td>
                    <span className={`adm-ledger-type ${entryColor(e.entryType)}`}>
                      {entryIcon(e.entryType)} {e.entryType?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="mono" style={{ fontSize: 11 }} title={e.triggerWallet || ''}>{e.triggerRoboId ? ident(e.triggerReferralCode, e.triggerWallet) : '—'}</td>
                  <td className="mono" style={{ fontSize: 11 }} title={e.beneficiaryWallet || ''}>{e.beneficiaryRoboId ? ident(e.beneficiaryReferralCode, e.beneficiaryWallet) : '—'}</td>
                  <td className="mono">{e.level || '—'}</td>
                  <td className={`mono ${e.entryType?.includes('out') || e.entryType?.includes('payout') || e.entryType?.includes('burn') || e.entryType?.includes('fee') ? 'text-red' : e.usdtAmount > 0 ? 'text-green' : ''}`}>
                    {e.usdtAmount ? <>{(e.entryType?.includes('out') || e.entryType?.includes('payout') || e.entryType?.includes('burn') || e.entryType?.includes('fee')) ? '-' : '+'}{mV(e.usdtAmount, 4)}</> : '—'}
                  </td>
                  <td className={`mono ${e.tokenAmount > 0 ? '' : ''}`}>
                    {e.tokenAmount ? f(e.tokenAmount, 2) : '—'}
                  </td>
                  <td className="mono" style={{ fontSize: 11 }}>{e.tokenPrice ? f(e.tokenPrice, 6) : '—'}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{e.totalSupplyAfter ? f(e.totalSupplyAfter, 2) : '—'}</td>
                  <td className="mono" style={{ fontSize: 11 }}>{e.liquidityAfter ? mV(e.liquidityAfter, 4) : '—'}</td>
                  <td style={{ fontSize: 11, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.description}>{e.description || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="d-empty">No ledger entries found for this filter.</div>
      )}

      {/* Pagination */}
      <Pagination page={page} pages={totalPages} onPage={handlePage} />
    </div>
  );
};

export default AdminLedger;